import { Link } from '@mui/material';
import { AxiosError } from 'axios';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import {
  p2pProvidersPayinRequestsApi,
  p2pProvidersPayoutRequestsApi,
} from 'api';
import { DescriptionRow } from 'components';
import { OrderType, QueryKey } from 'enums';
import { useMutation, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';

import { P2PProvidersRequestsDialog } from './P2PProvidersRequestsDialog';

const PAYIN_ORDER_LIST_QUERY_KEYS = [
  QueryKey.ActiveOrders,
  QueryKey.CompletedOrders,
  QueryKey.CancelledOrders,
  QueryKey.DisputeOrders,
  QueryKey.TraderDisputeOrders,
  QueryKey.ProviderDisputeOrders,
];

const PAYOUT_ORDER_LIST_QUERY_KEYS = [
  QueryKey.ActivePayoutOrders,
  QueryKey.CompletedPayoutOrders,
  QueryKey.CancelledPayoutOrders,
  QueryKey.DisputePayoutOrders,
  // add after it gets into release
  // QueryKey.ManualPayoutOrders,
];

type Props = {
  orderId: string;
  orderType: OrderType;
};

export const P2PProvidersRequests: React.FC<Props> = ({
  orderId,
  orderType,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.p2p_providers_requests',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { isAdminOrTechOperator } = useUser();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const queryKey = useMemo(
    () =>
      orderType === OrderType.Payin
        ? QueryKey.P2PProviderPayinRequests
        : QueryKey.P2PProviderPayoutRequests,
    [orderType],
  );
  const queryFn = useMemo(
    () =>
      orderType === OrderType.Payin
        ? p2pProvidersPayinRequestsApi.getForOrder
        : p2pProvidersPayoutRequestsApi.getForOrder,
    [orderType],
  );

  const orderListQueryKeys = useMemo(
    () =>
      orderType === OrderType.Payin
        ? PAYIN_ORDER_LIST_QUERY_KEYS
        : PAYOUT_ORDER_LIST_QUERY_KEYS,
    [orderType],
  );

  const queryResult = useQuery(queryKey, queryFn(orderId), {
    enabled: dialogOpen,
  });

  const pollMutation = useMutation<void, AxiosError, { orderId: string }>(
    orderType === OrderType.Payin
      ? p2pProvidersPayinRequestsApi.pollStatus
      : p2pProvidersPayoutRequestsApi.pollStatus,
    {
      notifierType: 'execute',
      onSuccess: () => {
        orderListQueryKeys.forEach((key) => queryClient.invalidateQueries(key));
      },
      onSettled: () => {
        void queryResult.refetch();
      },
    },
  );

  return (
    <Fragment>
      <DescriptionRow
        title={t('title')}
        value={
          <div>
            <Link
              component="button"
              variant="body2"
              underline="none"
              onClick={() => setDialogOpen(true)}
            >
              {tCommon('buttons.open')}
            </Link>
          </div>
        }
      />
      <P2PProvidersRequestsDialog
        queryResult={queryResult}
        orderId={orderId}
        pollMutation={isAdminOrTechOperator ? pollMutation : undefined}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Fragment>
  );
};
